import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./StepCabine.module.scss";
import InputForms from "components/UiComponents/forms/InputForms";
import { FaDesktop } from "react-icons/fa";

const CABINE_TYPES = [
  { value: "CONSULTING", label: "CONSULTING" },
  { value: "RECEPTION", label: "RECEPTION" },
  { value: "DEMONSTRATION", label: "DEMONSTRATION" },
];

const EMPTY_ADRESS = {
  Responsable: "",
  Tel: "",
  Adress: "",
  Adress2: "",
  CodePostal: "",
  Ville: "",
  Pays: "",
};

const StepCabine = ({ userData, updateUserData, onNext, onPrev, onValidChange }) => {
  const { t } = useTranslation();
  const [touchedCabines, setTouchedCabines] = useState({});
  const markCabineTouched = (idx) => setTouchedCabines((prev) => ({ ...prev, [idx]: true }));
  // Nombre de cabines choisi
  const [cabineCount, setCabineCount] = useState(
    Array.isArray(userData?.cabines) && userData.cabines.length > 0
      ? userData.cabines.length
      : 1
  );

  // Liste locale des cabines (édition)
  // ✅ Ajout des champs Adresse par cabine
  const [cabinesDraft, setCabinesDraft] = useState(() => {
    const existing = Array.isArray(userData?.cabines) ? userData.cabines : [];
    if (existing.length > 0) {
      return existing.map((c) => ({
        name: c?.name || "",
        type: c?.type || "CONSULTING",
        adress: {
          Responsable: c?.adress?.Responsable || "",
          Tel: c?.adress?.Tel || "",
          Adress: c?.adress?.Adress || "",
          Adress2: c?.adress?.Adress2 || "",
          CodePostal: c?.adress?.CodePostal || "",
          Ville: c?.adress?.Ville || "",
          Pays: c?.adress?.Pays || "",
        },
      }));
    }

    return [{ name: "", type: "CONSULTING", adress: { ...EMPTY_ADRESS } }];
  });

  // Resync si userData.cabines change de l'extérieur (seulement au montage ou vraie modification externe)
  useEffect(() => {
    const existing = Array.isArray(userData?.cabines) ? userData.cabines : [];
    // Seulement resync si cabinesDraft est vide ou si userData.cabines a une longueur différente
    if (cabinesDraft.length === 0 && existing.length > 0) {
      setCabineCount(existing.length);
      setCabinesDraft(
        existing.map((c) => ({
          name: c?.name || "",
          type: c?.type || "CONSULTING",
          adress: {
            Responsable: c?.adress?.Responsable || "",
            Tel: c?.adress?.Tel || "",
            Adress: c?.adress?.Adress || "",
            Adress2: c?.adress?.Adress2 || "",
            CodePostal: c?.adress?.CodePostal || "",
            Ville: c?.adress?.Ville || "",
            Pays: c?.adress?.Pays || "",
          },
        }))
      );
    }
  }, []);

  // Ajuste la taille du draft selon le nombre choisi
  useEffect(() => {
    setCabinesDraft((prev) => {
      const next = [...prev];

      // Ajouter si on augmente
      while (next.length < cabineCount) {
        next.push({
          name: "",
          type: "CONSULTING",
          adress: { ...EMPTY_ADRESS },
        });
      }

      // Couper si on diminue
      if (next.length > cabineCount) next.length = cabineCount;

      return next;
    });
  }, [cabineCount]);

  /**
   * ✅ IMPORTANT:
   * Tu veux pouvoir passer l'étape sans remplir l'adresse.
   * Donc l'adresse n'entre PAS dans la validation.
   * Ici on valide uniquement : nom + type (si tu veux aussi pouvoir passer sans nom, dis-moi).
   */
  const canSave = useMemo(() => {
    if (!cabinesDraft || cabinesDraft.length === 0) return true; // rien à saisir => on laisse passer
    return cabinesDraft.every(
      (c) => (c?.name || "").trim().length > 0 && (c?.type || "").length > 0
    );
  }, [cabinesDraft]);

  // Notify parent of validity
  useEffect(() => {
    if (!onValidChange) return;
    onValidChange(canSave);
  }, [canSave, onValidChange]);

  const updateCabineField = (index, field, value) => {
    setCabinesDraft((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateCabineAdressField = (index, field, value) => {
    setCabinesDraft((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        adress: {
          ...(next[index].adress || { ...EMPTY_ADRESS }),
          [field]: value,
        },
      };
      return next;
    });
  };

  // Mise à jour automatique des données dans le state parent
  useEffect(() => {
    const cleaned = cabinesDraft.map((c) => ({
      name: (c.name || "").trim(),
      type: c.type || "CONSULTING",
      adress: {
        Responsable: c?.adress?.Responsable || "",
        Tel: c?.adress?.Tel || "",
        Adress: c?.adress?.Adress || "",
        Adress2: c?.adress?.Adress2 || "",
        CodePostal: c?.adress?.CodePostal || "",
        Ville: c?.adress?.Ville || "",
        Pays: c?.adress?.Pays || "",
      },
    }));
    updateUserData({ cabines: cleaned });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinesDraft]);

  const handleSaveCabines = () => {
    const cleaned = cabinesDraft.map((c) => ({
      name: (c.name || "").trim(),
      type: c.type || "CONSULTING",
      // ✅ Adresse optionnelle : toujours sauvée mais peut être vide
      adress: {
        Responsable: c?.adress?.Responsable || "",
        Tel: c?.adress?.Tel || "",
        Adress: c?.adress?.Adress || "",
        Adress2: c?.adress?.Adress2 || "",
        CodePostal: c?.adress?.CodePostal || "",
        Ville: c?.adress?.Ville || "",
        Pays: c?.adress?.Pays || "",
      },
    }));

    // validation (nom obligatoire)
    if (cleaned.some((c) => !c.name)) return;

    updateUserData({ cabines: cleaned });
  };

  const handleNext = () => {
    // tu veux pouvoir passer même si adresse vide -> ok
    // mais si tu veux passer même si noms non remplis, dis-moi et je change la logique.
    if (!canSave) return;
    handleSaveCabines();
    onNext?.();
  };

  return (
    <div className={styles.container}>
      {/* Choix du nombre */}
      <div className={styles.countRow}>
        <label className={styles.label}>Nombre de cabines</label>
        <input
          type="number"
          value={cabineCount}
          onChange={(e) => {
            const val = e.target.value;
            // Permettre de laisser vide pour taper un nouveau chiffre
            if (val === '' || val === '0') {
              setCabineCount('');
            } else {
              const num = parseInt(val, 10);
              if (!isNaN(num)) {
                setCabineCount(Math.min(100, Math.max(1, num)));
              }
            }
          }}
          onBlur={(e) => {
            // Si vide après blur, revenir à 1
            if (e.target.value === '' || cabineCount === '') {
              setCabineCount(1);
            }
          }}
          min="1"
          max="100"
          className={styles.countInput}
        />
      </div>

      {/* Cabine par cabine */}
      <div className={styles.namesList}>
        {cabinesDraft.map((cab, idx) => (
          <div key={idx} className={styles.cabineCard} onBlur={() => markCabineTouched(idx)}>
            <div className={styles.cabineRow}>
              <InputForms
                type="text"
                placeholder={`Nom cabine ${idx + 1}`}
                value={cab.name}
                onChange={(e) => updateCabineField(idx, "name", e.target.value)}
                required
                svg={<FaDesktop />}
                paddingLeft="40px"
              />
              {touchedCabines[idx] && !(cab.name || "").trim() && (
                <span className={styles.fieldError}>Le nom de la cabine est obligatoire</span>
              )}

              <select
                value={cab.type}
                onChange={(e) => updateCabineField(idx, "type", e.target.value)}
              >
                {CABINE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ Adresse (optionnelle) */}
            <div className={styles.adressBlock}>
              <div className={styles.adressHeader}>
                <span className={styles.adressTitle}>Informations adresse (optionnel)</span>
                <button
                  type="button"
                  className={styles.clearAdress}
                  onClick={() =>
                    setCabinesDraft((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], adress: { ...EMPTY_ADRESS } };
                      return next;
                    })
                  }
                >
                  {t("subscription.clear_field")}
                </button>
              </div>

              <div className={styles.adressGrid}>
                <InputForms
                  type="text"
                  placeholder={t("subscription.manager")}
                  value={cab?.adress?.Responsable || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "Responsable", e.target.value)
                  }
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.phone")}
                  value={cab?.adress?.Tel || ""}
                  onChange={(e) => updateCabineAdressField(idx, "Tel", e.target.value)}
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.address")}
                  value={cab?.adress?.Adress || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "Adress", e.target.value)
                  }
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.address_2")}
                  value={cab?.adress?.Adress2 || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "Adress2", e.target.value)
                  }
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.postal_code")}
                  value={cab?.adress?.CodePostal || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "CodePostal", e.target.value)
                  }
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.city")}
                  value={cab?.adress?.Ville || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "Ville", e.target.value)
                  }
                />
                <InputForms
                  type="text"
                  placeholder={t("subscription.country")}
                  value={cab?.adress?.Pays || ""}
                  onChange={(e) =>
                    updateCabineAdressField(idx, "Pays", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepCabine;
